"use client"

import { AnimatePresence, MotionConfig, motion } from "framer-motion"
import { type ReactNode, useEffect, useRef } from "react"
import useMeasure from "react-use-measure"

type ContentAnimation = "scale" | "collapse" | "none"
type HeightAnimation = "always" | "view-change"

const contentAnimationProps = {
  scale: {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.96 },
  },
  collapse: {
    initial: {
      height: 0,
      clipPath: "inset(0 0 100% 0)",
    },
    animate: {
      height: "auto",
      clipPath: "inset(0 0 0% 0)",
    },
    exit: {
      height: 0,
      clipPath: "inset(0 0 100% 0)",
    },
    className: "overflow-hidden",
  },
  none: {},
} satisfies Record<ContentAnimation, object>

export function MeasuredHeightTransition({
  view,
  children,
  contentAnimation = "scale",
  heightAnimation = "always",
  skipInitial = false,
}: {
  view: string
  children: ReactNode
  contentAnimation?: ContentAnimation
  heightAnimation?: HeightAnimation
  skipInitial?: boolean
}) {
  const [contentRef, contentBounds] = useMeasure({ offsetSize: true })
  const hasMounted = useRef(false)
  const previousView = useRef(view)
  const previousMeasuredHeight = useRef(contentBounds.height)
  const pendingViewHeightAnimation = useRef(false)
  const shouldSkipInitial = useRef(skipInitial)
  const skippedInitialHeightAnimation = useRef(false)

  if (previousView.current !== view) {
    previousView.current = view
    pendingViewHeightAnimation.current = true
  }

  const shouldAnimateHeight =
    heightAnimation === "always" || pendingViewHeightAnimation.current
  const shouldSkipInitialHeightAnimation =
    shouldSkipInitial.current &&
    !skippedInitialHeightAnimation.current &&
    contentBounds.height > 0

  useEffect(() => {
    hasMounted.current = true
  }, [])

  useEffect(() => {
    if (contentBounds.height > 0) {
      skippedInitialHeightAnimation.current = true
    }

    if (previousMeasuredHeight.current !== contentBounds.height) {
      previousMeasuredHeight.current = contentBounds.height
      pendingViewHeightAnimation.current = false
    }
  }, [contentBounds.height])

  useEffect(() => {
    if (heightAnimation !== "view-change") return

    const timeout = window.setTimeout(() => {
      if (previousView.current === view) {
        pendingViewHeightAnimation.current = false
      }
    }, 100)

    return () => window.clearTimeout(timeout)
  }, [heightAnimation, view])

  return (
    <MotionConfig transition={{ duration: 0.3, type: "spring", bounce: 0 }}>
      <motion.div
        animate={{ height: hasMounted.current ? contentBounds.height : "auto" }}
        {...(shouldSkipInitialHeightAnimation || !shouldAnimateHeight
          ? { transition: { duration: 0 } }
          : {})}
        className="overflow-hidden"
      >
        <div ref={contentRef} className="flow-root">
          <AnimatePresence initial={false} mode="popLayout" custom={view}>
            <motion.div key={view} {...contentAnimationProps[contentAnimation]}>
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </MotionConfig>
  )
}
